import { useSeoMeta } from '@unhead/react';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { ArrowLeft, MessageCircle, Repeat2, CheckCircle, EyeOff } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';
import { nip19 } from 'nostr-tools';
import { cn } from '@/lib/utils';
import { PostAuthor } from '@/components/feed/PostAuthor';
import { PostActions } from '@/components/feed/PostActions';
import { NoteContent } from '@/components/NoteContent';
import { ReplyingToChip } from '@/components/feed/ReplyContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { useAuthor } from '@/hooks/useAuthor';
import { useEventById } from '@/hooks/useEventById';
import { useParentEvent } from '@/hooks/useParentEvent';
import { useMuteList } from '@/hooks/useMuteList';
import { useThreadChain } from '@/hooks/useThreadChain';
import { useReplyTree, countReplyNodes, type ReplyNode } from '@/hooks/useReplyTree';
import {
  getEventTitle,
  getCoverImage,
  getSummary,
  relativeTime,
  isLongform,
  isReply,
  isLivestream,
  getLivestreamInfo,
  getParentEventId,
  eventToNevent,
  tryExtractEmbeddedEvent,
  isCommunityApproval,
  findRelayHintForEvent,
  stripMediaUrls,
} from '@/lib/postUtils';

interface EventDetailPageProps {
  eventId: string;
  pubkey?: string;
  /** For addressable events — the kind number (used with pubkey + d-tag) */
  kind?: number;
  /** Relay URLs from the NIP-19 identifier that are known to have this event. */
  relays?: string[];
}

export function EventDetailPage({ eventId, pubkey, kind, relays }: EventDetailPageProps) {
  const { data: outerEvent, isLoading } = useEventById({
    eventId,
    pubkey,
    kind,
    relayHints: relays,
    timeoutMs: 8000,
    staleTime: 5 * 60 * 1000,
  });

  // Reposts and community approvals wrap the real post as JSON in content.
  const event = outerEvent ? (tryExtractEmbeddedEvent(outerEvent) ?? outerEvent) : null;
  const wrapper = outerEvent && tryExtractEmbeddedEvent(outerEvent) ? outerEvent : null;

  useSeoMeta({
    title: event
      ? `${getEventTitle(event) ?? event.content.slice(0, 50)} · Mixstr`
      : 'Post · Mixstr',
  });

  return (
    <div className="max-w-2xl mx-auto">
      <div className="sticky top-0 z-10 bg-background/90 backdrop-blur border-b border-border">
        <div className="px-4 py-3 flex items-center gap-3">
          <BackButton />
          <h1 className="text-base font-bold">Post</h1>
        </div>
      </div>

      {isLoading && <EventDetailSkeleton />}

      {!isLoading && !event && (
        <Card className="border-dashed mx-4 my-8">
          <CardContent className="py-12 text-center text-muted-foreground text-sm">
            Post not found or could not be loaded.
          </CardContent>
        </Card>
      )}

      {/* key resets thread state (scroll position, chain cache refs) when
          navigating between two posts without unmounting the page */}
      {event && <EventDetailBody key={event.id} event={event} wrapper={wrapper} />}
    </div>
  );
}

function BackButton() {
  const navigate = useNavigate();
  return (
    <Button variant="ghost" size="icon" className="w-8 h-8" onClick={() => navigate(-1)}>
      <ArrowLeft size={18} />
    </Button>
  );
}

function EventDetailBody({ event, wrapper }: { event: NostrEvent; wrapper?: NostrEvent | null }) {
  const navigate = useNavigate();
  const longform = isLongform(event);
  const title = getEventTitle(event);
  const cover = getCoverImage(event);
  const summary = getSummary(event);

  // Redirect to naddr for livestream events
  const isLive = isLivestream(event);
  const livestreamInfo = isLive ? getLivestreamInfo(event) : null;
  const livestreamNaddr = livestreamInfo?.dTag
    ? nip19.naddrEncode({ kind: 30311, pubkey: event.pubkey, identifier: livestreamInfo.dTag })
    : null;

  // Perform redirect for livestreams using useEffect
  useEffect(() => {
    if (isLive && livestreamNaddr) {
      navigate(`/${livestreamNaddr}`, { replace: true });
    }
  }, [isLive, livestreamNaddr, navigate]);

  // Hooks must run on every render regardless of the livestream early-return
  // below, otherwise React sees a different hook count between renders.
  const reply = isReply(event);

  // Full ancestor chain above this post (root first).
  const { data: ancestors, isPending: chainPending } = useThreadChain(reply ? event : null);

  // Fallback for the degenerate case where the chain couldn't resolve the
  // direct parent at all: show the compact "Replying to" chip instead.
  const parentRef = reply ? getParentEventId(event) : null;
  const chainExhausted = reply && !!ancestors && ancestors.length === 0;
  const { data: parentEvent, isPending: parentPending } = useParentEvent(
    chainExhausted ? parentRef : null,
  );

  // Bring the focused post into view once a long ancestor chain loads.
  const focusRef = useRef<HTMLDivElement>(null);
  const scrolledRef = useRef(false);
  useEffect(() => {
    if (scrolledRef.current || !ancestors || ancestors.length < 3) return;
    scrolledRef.current = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = focusRef.current;
        const scroller = document.getElementById('main-scroll');
        if (!el || !scroller) return;
        const top = el.getBoundingClientRect().top - 60; // sticky header offset
        if (top > 0) scroller.scrollBy({ top });
      });
    });
  }, [ancestors]);

  // Show minimal content while redirecting
  if (isLive && livestreamNaddr) {
    return (
      <div className="px-4 py-5 border-b border-border">
        <p className="text-sm text-muted-foreground">Redirecting to livestream page...</p>
      </div>
    );
  }

  return (
    <>
      {/* Full conversation above the focused post */}
      {reply && (
        <ThreadAncestors
          event={event}
          ancestors={ancestors}
          isPending={chainPending}
          fallbackParentRef={parentRef}
          fallbackParent={chainExhausted ? parentEvent : null}
          fallbackPending={parentPending}
        />
      )}

      {/* Focused post — highlighted so it's clear which message was opened */}
      <div className="px-3 pt-4 pb-5 border-b border-border">
        <div
          ref={focusRef}
          className="rounded-xl ring-1 ring-primary/40 bg-primary/5 px-4 py-4 scroll-mt-16"
        >
          {/* Wrapper banner (repost / community approval) */}
          {wrapper && <WrapperBanner wrapper={wrapper} />}

          <PostAuthor pubkey={event.pubkey} createdAt={event.created_at} />

          <div className="mt-3 space-y-3">
            {longform ? (
              <>
                {cover && (
                  <img src={cover} alt={title} className="w-full rounded-xl object-cover max-h-80" loading="lazy" />
                )}
                {title && <h1 className="text-xl font-bold text-foreground leading-snug">{title}</h1>}
                {summary && <p className="text-sm text-muted-foreground italic">{summary}</p>}
                <div className="prose prose-invert prose-sm max-w-none">
                  <NoteContent event={event} inlineExternalEmbeds />
                </div>
              </>
            ) : (
              <NoteContent event={event} className="text-base leading-relaxed" inlineExternalEmbeds />
            )}
          </div>

          {/* Full timestamp */}
          <p className="text-xs text-muted-foreground mt-4">
            {new Date(event.created_at * 1000).toLocaleString()}
          </p>

          <div className="mt-3 pt-3 border-t border-border/60">
            <PostActions event={event} />
          </div>
        </div>
      </div>

      {/* Threaded replies below */}
      <ReplyThread rootId={event.id} />
    </>
  );
}

/**
 * Renders the full ancestor chain above the focused post (root first), each
 * row compact and clickable to re-center the thread on that post.
 */
function ThreadAncestors({
  event,
  ancestors,
  isPending,
  fallbackParentRef,
  fallbackParent,
  fallbackPending,
}: {
  /** The focused post — used to find relay hints for the direct parent. */
  event: NostrEvent;
  ancestors: NostrEvent[] | undefined;
  isPending: boolean;
  fallbackParentRef: { id: string; relay?: string; author?: string } | null;
  fallbackParent: NostrEvent | null | undefined;
  fallbackPending: boolean;
}) {
  const { isMuted } = useMuteList();

  if (isPending) {
    return (
      <div className="border-b border-border px-4 pt-3 pb-2 space-y-2.5">
        <Skeleton className="h-3 w-44" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-2/3" />
      </div>
    );
  }

  if (!ancestors || ancestors.length === 0) {
    if (!fallbackParentRef) return null;
    // Chain didn't resolve — keep the compact chip as context.
    return (
      <div className="border-b border-border pb-2">
        <ReplyingToChip
          parentId={fallbackParentRef.id}
          parent={fallbackParent ?? undefined}
          isPending={fallbackPending}
        />
      </div>
    );
  }

  return (
    <div className="border-b border-border" aria-label="Conversation above this post">
      {ancestors.map((ancestor, i) => {
        // The child event in the chain may carry a relay hint for this
        // ancestor — forward it so navigation resolves quickly.
        const child = i + 1 < ancestors.length ? ancestors[i + 1] : event;
        const relayHint = findRelayHintForEvent(child, ancestor.id);
        return (
          <ThreadAncestorItem
            key={ancestor.id}
            event={ancestor}
            muted={isMuted(ancestor)}
            relayHint={relayHint}
          />
        );
      })}
    </div>
  );
}

function ThreadAncestorItem({
  event,
  muted,
  relayHint,
}: {
  event: NostrEvent;
  muted: boolean;
  relayHint?: string;
}) {
  const navigate = useNavigate();
  const author = useAuthor(muted ? undefined : event.pubkey);
  const meta = author.data?.metadata;
  const rawName = meta?.display_name || meta?.name || '';
  const displayName = rawName.trim() || event.pubkey.slice(0, 10) + '…';
  const npub = nip19.npubEncode(event.pubkey);

  const previewText = stripMediaUrls(event.content)
    .replace(/https?:\/\/\S+/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 160);

  return (
    <div
      className={cn(
        'px-4 pt-3 transition-colors',
        !muted && 'cursor-pointer hover:bg-accent/20',
      )}
      onClick={(e) => {
        e.stopPropagation();
        if (!muted) {
          navigate(`/${eventToNevent(event, relayHint ? [relayHint] : undefined)}`);
        }
      }}
    >
      <div className="flex gap-3">
        <div className="flex flex-col items-center flex-shrink-0">
          {muted ? (
            <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
              <EyeOff size={14} className="text-muted-foreground" aria-hidden="true" />
            </div>
          ) : (
            <Link to={`/${npub}`} onClick={(e) => e.stopPropagation()}>
              <Avatar className="w-9 h-9">
                <AvatarImage src={meta?.picture} />
                <AvatarFallback className="text-xs bg-muted-foreground/20 text-muted-foreground font-bold">
                  {displayName[0]?.toUpperCase() ?? '?'}
                </AvatarFallback>
              </Avatar>
            </Link>
          )}
          {/* Thread connector down to the next post in the chain */}
          <div className="w-0.5 bg-border/60 flex-1 mt-1.5 min-h-[20px]" />
        </div>

        <div className="flex-1 min-w-0 pb-2">
          {muted ? (
            <p className="text-xs text-muted-foreground italic py-2">Post hidden (muted)</p>
          ) : (
            <>
              <div className="flex items-baseline gap-1.5 flex-wrap">
                <Link
                  to={`/${npub}`}
                  onClick={(e) => e.stopPropagation()}
                  className="font-semibold text-sm text-foreground hover:text-primary transition-colors truncate"
                >
                  {displayName}
                </Link>
                <span className="text-xs text-muted-foreground flex-shrink-0">
                  {relativeTime(event.created_at)}
                </span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 mt-0.5">
                {previewText || <span className="italic">Media or attachment</span>}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function WrapperBanner({ wrapper }: { wrapper: NostrEvent }) {
  const author = useAuthor(wrapper.pubkey);
  const meta = author.data?.metadata;
  const rawName = meta?.display_name || meta?.name || '';
  const name = rawName.trim() || wrapper.pubkey.slice(0, 10) + '…';
  const isApproval = isCommunityApproval(wrapper);

  return (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground pb-2">
      {isApproval ? <CheckCircle size={13} /> : <Repeat2 size={13} />}
      <Link
        to={`/${eventToNevent(wrapper)}`}
        onClick={(e) => e.stopPropagation()}
        className="hover:underline"
      >
        {name}
      </Link>
      <span>{isApproval ? 'approved this post' : 'reposted this post'}</span>
    </div>
  );
}

/**
 * Threaded replies below the focused post. Replies are nested up to a few
 * levels; anything deeper is reached by opening that reply's own thread view.
 */
function ReplyThread({ rootId }: { rootId: string }) {
  const { isMuted, isLoading: isBlocklistLoading } = useMuteList();
  const { data: nodes = [], isPending } = useReplyTree(rootId);

  // Wait for both the blocklist and replies to load before displaying
  const isLoading = isBlocklistLoading || isPending;
  const total = countReplyNodes(nodes);

  return (
    <div>
      <div className="px-4 py-3 flex items-center gap-2 border-b border-border">
        <MessageCircle size={16} className="text-primary" />
        <h2 className="text-sm font-semibold text-foreground">
          {isLoading ? 'Loading replies…' : `${total} ${total === 1 ? 'reply' : 'replies'}`}
        </h2>
      </div>

      {isLoading && (
        <div className="space-y-0">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-3 px-4 py-4 border-b border-border">
              <Skeleton className="w-9 h-9 rounded-full flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3 w-32" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-3/4" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && nodes.length === 0 && (
        <Card className="border-dashed mx-4 my-6">
          <CardContent className="py-8 text-center text-muted-foreground text-sm">
            No replies yet. Be the first to reply!
          </CardContent>
        </Card>
      )}

      {!isLoading &&
        nodes.map((node) => (
          <ReplyNodeItem key={node.event.id} node={node} depth={0} isMuted={isMuted} />
        ))}
    </div>
  );
}

/** Visual indent stops after this depth so mobile widths stay usable. */
const MAX_VISUAL_DEPTH = 3;

function ReplyNodeItem({
  node,
  depth,
  isMuted,
}: {
  node: ReplyNode;
  depth: number;
  isMuted: (event: NostrEvent) => boolean;
}) {
  const navigate = useNavigate();
  const muted = isMuted(node.event);
  const author = useAuthor(muted ? undefined : node.event.pubkey);
  const meta = author.data?.metadata;
  const rawName = meta?.display_name || meta?.name || '';
  const displayName = rawName.trim() || node.event.pubkey.slice(0, 10) + '…';
  const npub = nip19.npubEncode(node.event.pubkey);

  const hasChildren = node.children.length > 0;
  const indents = depth < MAX_VISUAL_DEPTH;

  return (
    <div>
      {/* Reply row — clicking re-centers the thread view on this reply */}
      <div
        className="flex gap-3 px-4 py-3 cursor-pointer hover:bg-accent/20 transition-colors"
        onClick={() => navigate(`/${eventToNevent(node.event)}`)}
      >
        <div className="flex flex-col items-center flex-shrink-0">
          {muted ? (
            <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
              <EyeOff size={14} className="text-muted-foreground" aria-hidden="true" />
            </div>
          ) : (
            <Link to={`/${npub}`} onClick={(e) => e.stopPropagation()}>
              <Avatar className="w-9 h-9">
                <AvatarImage src={meta?.picture} />
                <AvatarFallback className="text-xs bg-primary/20 text-primary font-bold">
                  {displayName[0]?.toUpperCase() ?? '?'}
                </AvatarFallback>
              </Avatar>
            </Link>
          )}
          {hasChildren && indents && (
            <div className="w-0.5 bg-border/60 flex-1 mt-1.5 min-h-[16px]" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <Link
              to={`/${npub}`}
              onClick={(e) => e.stopPropagation()}
              className="text-sm font-semibold text-foreground hover:text-primary transition-colors"
            >
              {muted ? 'Muted user' : displayName}
            </Link>
            <span className="text-xs text-muted-foreground">{relativeTime(node.event.created_at)}</span>
          </div>
          <div className="mt-1">
            {muted ? (
              <p className="text-xs text-muted-foreground italic">Reply hidden (muted)</p>
            ) : (
              <NoteContent event={node.event} className="text-sm" />
            )}
          </div>
          <div className="mt-2" onClick={(e) => e.stopPropagation()}>
            <PostActions event={node.event} compact />
          </div>
        </div>
      </div>

      {/* Nested replies */}
      {hasChildren && (
        <div className={indents ? 'ml-[26px] pl-2 border-l border-border/60' : undefined}>
          {node.children.map((child) => (
            <ReplyNodeItem
              key={child.event.id}
              node={child}
              depth={Math.min(depth + 1, MAX_VISUAL_DEPTH)}
              isMuted={isMuted}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function EventDetailSkeleton() {
  return (
    <div className="px-4 py-5 border-b border-border space-y-4">
      <div className="flex gap-3">
        <Skeleton className="w-10 h-10 rounded-full" />
        <div className="space-y-1.5 flex-1">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-4 w-4/6" />
      <Skeleton className="h-48 w-full rounded-xl" />
    </div>
  );
}
